<template>
	<ul ref="horizontal" class="vcx-horizontal" :style="{ gap: `${cardDisplayGap}px` }" v-bind="$attrs">
		<slot name="scroll-items"> </slot>
	</ul>
	<div
		class="vcx-display-none-smo vcx-flex-md vcx-gap-200 vcx-justify-center vcx-mt-150"
		:class="{ 'vcx-horizontal__scroll-controls--hide': arrivedState.left && arrivedState.right }"
	>
		<BaseButtonIconOnly
			classes="vcx-button-icon-only"
			icon="chevron"
			icon-dir="left"
			aria-label="Scroll left"
			:disabled="arrivedState.left"
			@click="x -= scrollAmount"
		/>
		<BaseButtonIconOnly
			classes="vcx-button-icon-only"
			icon="chevron"
			icon-dir="right"
			aria-label="Scroll right"
			:disabled="arrivedState.right"
			@click="x += scrollAmount"
		/>
	</div>
</template>

<script setup lang="ts">
import { useDebounceFn, useEventListener, useMutationObserver, useScroll } from '@vueuse/core';
import { computed, nextTick, useTemplateRef } from 'vue';
import { useAppStore } from '@stores/app';

const props = withDefaults(
	defineProps<{
		cardWidth?: number;
		cardGap?: number;
	}>(),
	{
		cardWidth: 400,
		cardGap: 48
	}
);

const appStore = useAppStore();

const horizontal = useTemplateRef<HTMLElement>('horizontal');
const { x, arrivedState, measure } = useScroll(horizontal, { behavior: 'smooth' });
useEventListener(window, 'resize', () => nextTick(measure));
const debouncedMeasure = useDebounceFn(() => nextTick(measure), 50);
useMutationObserver(horizontal, debouncedMeasure, { childList: true }); // re-measure on slot content change

const cardDisplayWidth = computed(() => {
	return appStore.isMobile ? props.cardWidth * 0.8 : props.cardWidth;
});

const cardDisplayGap = computed(() => {
	return appStore.isMobile ? props.cardGap / 3 : props.cardGap;
});

const scrollAmount = computed(() => cardDisplayWidth.value + cardDisplayGap.value);
</script>

<style scoped lang="scss">
@use '../../styles/vcx/responsive.scss' as *;

.vcx-horizontal {
	display: flex;
	align-items: stretch;
	overflow-x: scroll;
	overscroll-behavior-x: contain;
	pointer-events: auto;
	scrollbar-width: none;
	flex-wrap: nowrap;
	transition: all 0.2s;

	&__scroll-controls--hide {
		opacity: 0;
		pointer-events: none;
		transition: opacity 0.2s;
	}
}
</style>
